/**
 * La letra de los botones, atada a la del párrafo.
 *
 * Un paso por debajo del párrafo —7/8, redondeado: 16 → 14, 17 → 15, 18 → 16—,
 * como en las webs de producto: igual que el párrafo se leían como texto y no
 * como acción, y con un cuerpo suelto —13px al lado de un párrafo de 17px—
 * parecían de otra página. Van así en cada tamaño de pantalla y siguen al
 * párrafo cuando cambia. Ningún estilo global lo toca; sólo quien lo cambie a
 * mano.
 *
 * «Lo siguen» se decide por igualdad: un botón con el tamaño que le tocaba se
 * mueve con el párrafo; uno con otro tamaño es que alguien lo eligió, y se
 * queda.
 */
import type { StyleProperties } from '@webbuilder/style-engine'
import type { ProjectTheme } from './types'
import { mergeStylePatch } from './stylePatch'

type Bp = 'desktop' | 'tablet' | 'mobile'
const SMALL: Exclude<Bp, 'desktop'>[] = ['tablet', 'mobile']

const BUTTON_RATIO = 7 / 8

/** El cuerpo del botón para un párrafo dado; lo que no sea px o rem se deja igual. */
export function buttonSizeFor(paragraph: string | undefined): string | undefined {
  if (!paragraph) return paragraph
  const px = /^(\d+(?:\.\d+)?)px$/.exec(paragraph.trim())?.[1]
  const rem = /^(\d+(?:\.\d+)?)rem$/.exec(paragraph.trim())?.[1]
  const value = px ? Number(px) : rem ? Number(rem) * 16 : null
  return value === null ? paragraph : `${Math.round(value * BUTTON_RATIO)}px`
}

/** Lo que le toca al botón en cada pantalla, según el párrafo. */
export function buttonSizes(theme: ProjectTheme): Record<Bp, string | undefined> {
  const paragraph = paragraphSizes(theme)
  return { desktop: buttonSizeFor(paragraph.desktop), tablet: buttonSizeFor(paragraph.tablet), mobile: buttonSizeFor(paragraph.mobile) }
}

/** El tamaño del párrafo en cada pantalla (lo que hereda si no tiene propio). */
export function paragraphSizes(theme: ProjectTheme): Record<Bp, string | undefined> {
  const desktop = theme.roles.paragraph.fontSize
  const tablet = theme.roleBreakpoints?.tablet?.paragraph?.fontSize ?? desktop
  const mobile = theme.roleBreakpoints?.mobile?.paragraph?.fontSize ?? tablet
  return { desktop, tablet, mobile }
}

function buttonSize(styles: StyleProperties | undefined, breakpoints: Partial<Record<'tablet' | 'mobile', Partial<StyleProperties>>> | undefined, bp: Bp) {
  if (bp === 'desktop') return styles?.fontSize
  const tablet = breakpoints?.tablet?.fontSize ?? styles?.fontSize
  return bp === 'tablet' ? tablet : breakpoints?.mobile?.fontSize ?? tablet
}

/**
 * Pasa a `next` los botones cuya letra valía `previous` —los que seguían al
 * párrafo—. Sin `previous`, los pasa todos: es lo que hace un estilo de
 * Combinations, que deja la página entera recién ordenada.
 */
export function alignButtonText(
  theme: ProjectTheme,
  next: Record<Bp, string | undefined>,
  previous?: Record<Bp, string | undefined>,
): ProjectTheme {
  let roles = theme.roles
  let roleBreakpoints = theme.roleBreakpoints
  const buttonComponents = theme.buttonComponents.map((item) => {
    let styles = item.styles
    let breakpoints = item.breakpoints
    for (const bp of ['desktop', ...SMALL] as Bp[]) {
      const size = next[bp]
      if (!size) continue
      const current = buttonSize(styles, breakpoints, bp)
      if (previous && current !== previous[bp]) continue
      if (current === size) continue
      const patch = { fontSize: size }
      if (bp === 'desktop') {
        styles = mergeStylePatch(styles, patch)
        if (item.role) roles = { ...roles, [item.role]: mergeStylePatch(roles[item.role], patch) }
      } else {
        breakpoints = { ...breakpoints, [bp]: mergeStylePatch(breakpoints?.[bp], patch) }
        if (item.role) {
          roleBreakpoints = {
            ...roleBreakpoints,
            [bp]: { ...roleBreakpoints?.[bp], [item.role]: mergeStylePatch(roleBreakpoints?.[bp]?.[item.role], patch) },
          }
        }
      }
    }
    return styles === item.styles && breakpoints === item.breakpoints ? item : { ...item, styles, ...(breakpoints ? { breakpoints } : {}) }
  })
  return { ...theme, roles, ...(roleBreakpoints ? { roleBreakpoints } : {}), buttonComponents }
}

/**
 * Tras un cambio en el párrafo: los botones que lo seguían se mueven con él.
 * `before` es el tema de antes del cambio.
 */
export function followParagraph(before: ProjectTheme, after: ProjectTheme): ProjectTheme {
  const previous = buttonSizes(before)
  const next = buttonSizes(after)
  if ((['desktop', 'tablet', 'mobile'] as Bp[]).every((bp) => previous[bp] === next[bp])) return after
  return alignButtonText(after, next, previous)
}
